import { Directive, ElementRef, Renderer2, Input, OnInit, SimpleChanges, OnChanges } from '@angular/core';

// Object Interface
export interface LiveRegionConfig {
  elementId?: string;
  ariaAtomic?: boolean;
  ariaLive?: 'off' | 'polite' | 'assertive';
  elementTag?: 'span' | 'pre' | 'p';
  liveMessage: string;
  useHostAsLiveRegion?: boolean;
}

@Directive({
  selector: '[lm-live-region]',
  standalone: false,
})
export class LMLiveRegionDirective implements OnInit, OnChanges {
  private liveRegionEl!: HTMLElement;

  // Default configuration values for values that are not supplied
  private defaultConfig: LiveRegionConfig = {
    elementTag: 'span',
    ariaAtomic: false,
    ariaLive: 'polite',
    liveMessage: '',
    useHostAsLiveRegion: false
  };

  private liveRegionConfigObj: LiveRegionConfig = { ...this.defaultConfig };

  @Input() set liveRegionConfig(value: string | LiveRegionConfig) {
    // If the input is a plain string, set it only for the liveMessage property
    if (typeof value === 'string') {
      this.liveRegionConfigObj = { ...this.defaultConfig, liveMessage: value };
    } else {
      this.liveRegionConfigObj = { ...this.defaultConfig, ...value };
    }
  }

  constructor(private element: ElementRef, private renderer: Renderer2) {}

  ngOnInit(): void {
    // Check if the host element itself should be used as the live region
    if (this.liveRegionConfigObj.useHostAsLiveRegion) {
      this.makeHostElementLiveRegion();
    } else {
      this.createOrUpdateLiveRegionElement();
    }

    // Set the live message after the element is created/updated
    this.updateLiveRegion(this.liveRegionConfigObj.liveMessage);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['liveRegionConfig']) {
      const prevConfig = changes['liveRegionConfig'].previousValue;
      const currConfig = this.liveRegionConfigObj;

      // Check if the elementTag has changed (in case of custom element tag)
      const shouldRecreateElement = prevConfig?.elementTag !== currConfig.elementTag;

      // If the host is being used, no need to recreate elements
      if (!currConfig.useHostAsLiveRegion && shouldRecreateElement) {
        this.createOrUpdateLiveRegionElement();
      }

      // Update aria-live and aria-atomic attributes if they've changed for the existing element
      if (prevConfig?.ariaLive !== currConfig.ariaLive || prevConfig?.ariaAtomic !== currConfig.ariaAtomic) {
        this.updateAriaAttributes(currConfig);
      }

      this.updateLiveRegion(currConfig.liveMessage);
    }
  }

  private makeHostElementLiveRegion(): void {
    // Update aria-live and aria-atomic attributes directly on the host element
    this.renderer.addClass(this.element.nativeElement, 'audible');
    this.renderer.setAttribute(this.element.nativeElement, 'aria-live', this.liveRegionConfigObj.ariaLive ?? 'polite');
    this.renderer.setAttribute(this.element.nativeElement, 'aria-atomic', String(this.liveRegionConfigObj.ariaAtomic ?? false));
  }

  private createOrUpdateLiveRegionElement(): void {
    const config = this.liveRegionConfigObj;

    // Optional: if there is a scenario where it is needed to change the element tagging, remove the existing element
    if (this.liveRegionEl) {
      this.renderer.removeChild(this.element.nativeElement, this.liveRegionEl);
    }

    // Creating the element for live region and applying attributes based on the input
    this.liveRegionEl = this.renderer.createElement(config.elementTag || 'span');
    this.renderer.addClass(this.liveRegionEl, 'audible');
    this.renderer.setAttribute(this.liveRegionEl, 'aria-atomic', String(config.ariaAtomic ?? false));
    this.renderer.setAttribute(this.liveRegionEl, 'aria-live', config.ariaLive ?? 'polite');

    // Optional: Element ID if needed
    if (config.elementId) {
      this.renderer.setAttribute(this.liveRegionEl, 'id', config.elementId);
    }

    // Append the new element to the parent element
    this.renderer.appendChild(this.element.nativeElement, this.liveRegionEl);
  }

  private updateAriaAttributes(config: LiveRegionConfig): void {
    if (this.liveRegionEl) {
      this.renderer.setAttribute(this.liveRegionEl, 'aria-live', config.ariaLive ?? 'polite');
      this.renderer.setAttribute(this.liveRegionEl, 'aria-atomic', String(config.ariaAtomic ?? false));
    } else {
      // If host is used as the live region, apply to host directly
      this.renderer.setAttribute(this.element.nativeElement, 'aria-live', config.ariaLive ?? 'polite');
      this.renderer.setAttribute(this.element.nativeElement, 'aria-atomic', String(config.ariaAtomic ?? false));
    }
  }

  private updateLiveRegion(message: string): void {
    if (this.liveRegionEl) {
      // Clear existing content to force a screen reader re-announcement
      this.liveRegionEl.textContent = '';
      setTimeout(() => {
        if (this.liveRegionEl) {
          // Set the new message after a slight delay
          this.liveRegionEl.textContent = message;
        }
      }, 150); // Small delay ensures proper screen reader announcement
    } else if (this.element.nativeElement) {
      // If host is used as the live region, update the host's content
      this.element.nativeElement.textContent = message;
    }
  }
}
